// macadamia-app/src/macadamia-core/components/hooks/useHistory.ts
import { useState, useCallback, useRef } from 'react';
import { produceWithPatches, applyPatches, Patch, enablePatches } from 'immer';

// Activer le plugin Patches d'Immer (requis pour Immer v9+)
enablePatches();

interface HistoryEntry {
    patches: Patch[];
    inversePatches: Patch[];
    timestamp: number;
}

interface UseHistoryOptions {
    maxHistorySize?: number;
    debounceMs?: number;
}

interface UseHistoryReturn<T> {
    state: T;
    setState: (updater: T | ((draft: T) => void)) => void;
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
    clearHistory: () => void;
    historySize: number;
}

/**
 * Hook personnalisé pour gérer l'historique undo/redo avec Immer patches
 *
 * @param initialState - État initial
 * @param options - Options de configuration
 * @returns État, setters, et fonctions undo/redo
 */
export function useHistory<T>(
    initialState: T,
    options: UseHistoryOptions = {}
): UseHistoryReturn<T> {
    const { maxHistorySize = 100, debounceMs = 0 } = options;

    const [state, setInternalState] = useState<T>(initialState);
    const [undoStack, setUndoStack] = useState<HistoryEntry[]>([]);
    const [redoStack, setRedoStack] = useState<HistoryEntry[]>([]);

    // Timer pour debounce
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const pendingPatchesRef = useRef<{
        patches: Patch[];
        inversePatches: Patch[];
        timestamp: number;
    } | null>(null);

    /**
     * Ajouter une entrée à l'historique undo
     */
    const pushToUndoStack = useCallback((entry: HistoryEntry) => {
        setUndoStack(prev => {
            const newStack = [...prev, entry];
            // Limiter la taille de l'historique
            if (newStack.length > maxHistorySize) {
                return newStack.slice(newStack.length - maxHistorySize);
            }
            return newStack;
        });
        // Vider le redo stack à chaque nouvelle action
        setRedoStack([]);
    }, [maxHistorySize]);

    /**
     * Flush les patches en attente (pour debounce)
     */
    const flushPendingPatches = useCallback(() => {
        if (pendingPatchesRef.current) {
            pushToUndoStack(pendingPatchesRef.current);
            pendingPatchesRef.current = null;
        }
    }, [pushToUndoStack]);

    /**
     * Setter principal - enregistre les patches dans l'historique
     */
    const setState = useCallback((updater: T | ((draft: T) => void)) => {
        setInternalState(currentState => {
            // Si updater est une fonction, utiliser produceWithPatches
            if (typeof updater === 'function') {
                const [nextState, patches, inversePatches] = produceWithPatches(
                    currentState,
                    updater as (draft: T) => void
                );

                // Si pas de changement, ne rien faire
                if (patches.length === 0) {
                    return currentState;
                }

                const entry: HistoryEntry = {
                    patches,
                    inversePatches,
                    timestamp: Date.now()
                };

                if (debounceMs > 0) {
                    // Mode debounce : accumuler les patches
                    if (debounceTimerRef.current) {
                        clearTimeout(debounceTimerRef.current);
                    }

                    if (pendingPatchesRef.current) {
                        // Fusionner avec les patches en attente
                        pendingPatchesRef.current = {
                            patches: [...pendingPatchesRef.current.patches, ...patches],
                            inversePatches: [...inversePatches, ...pendingPatchesRef.current.inversePatches],
                            timestamp: pendingPatchesRef.current.timestamp
                        };
                    } else {
                        pendingPatchesRef.current = entry;
                    }

                    debounceTimerRef.current = setTimeout(() => {
                        flushPendingPatches();
                    }, debounceMs);
                } else {
                    // Mode immédiat
                    pushToUndoStack(entry);
                }

                return nextState;
            } else {
                // Si updater est un état direct, créer des patches
                const [nextState, patches, inversePatches] = produceWithPatches(
                    currentState,
                    () => updater
                );

                if (patches.length === 0) {
                    return currentState;
                }

                const entry: HistoryEntry = {
                    patches,
                    inversePatches,
                    timestamp: Date.now()
                };

                pushToUndoStack(entry);
                return nextState;
            }
        });
    }, [debounceMs, pushToUndoStack, flushPendingPatches]);

    /**
     * Undo - Appliquer les patches inverses
     */
    const undo = useCallback(() => {
        // Flush les patches en attente avant undo
        flushPendingPatches();

        if (undoStack.length === 0) {
            console.warn('useHistory: Aucune action à annuler');
            return;
        }

        const entry = undoStack[undoStack.length - 1];

        setInternalState((currentState): T => {
            // Garde de type : si null, impossible d'appliquer des patches
            if (currentState === null) {
                return currentState;
            }
            try {
                return applyPatches(currentState as any, entry.inversePatches) as T;
            } catch (error) {
                console.error('useHistory: Erreur lors de l\'undo', error);
                return currentState;
            }
        });

        // Déplacer l'entrée du undo stack vers le redo stack
        setUndoStack(prev => prev.slice(0, -1));
        setRedoStack(prev => [...prev, entry]);
    }, [undoStack, flushPendingPatches]);

    /**
     * Redo - Réappliquer les patches
     */
    const redo = useCallback(() => {
        if (redoStack.length === 0) {
            console.warn('useHistory: Aucune action à rétablir');
            return;
        }

        const entry = redoStack[redoStack.length - 1];

        setInternalState((currentState): T => {
            // Garde de type : si null, impossible d'appliquer des patches
            if (currentState === null) {
                return currentState;
            }
            try {
                return applyPatches(currentState as any, entry.patches) as T;
            } catch (error) {
                console.error('useHistory: Erreur lors du redo', error);
                return currentState;
            }
        });

        // Déplacer l'entrée du redo stack vers le undo stack
        setRedoStack(prev => prev.slice(0, -1));
        setUndoStack(prev => [...prev, entry]);
    }, [redoStack]);

    /**
     * Vider l'historique
     */
    const clearHistory = useCallback(() => {
        flushPendingPatches();
        setUndoStack([]);
        setRedoStack([]);
    }, [flushPendingPatches]);

    return {
        state,
        setState,
        undo,
        redo,
        canUndo: undoStack.length > 0,
        canRedo: redoStack.length > 0,
        clearHistory,
        historySize: undoStack.length
    };
}
